import { ApolloLink } from '@apollo/client/core/index.js'

import { Observable, map } from 'rxjs'
import type { GraphQLFormattedError } from 'graphql'

export function apolloLogger(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const time = new Date().getTime()
    return forward(operation).pipe(
      map((res) => {
        if (import.meta.dev) {
          const log = res?.extensions?.log as Record<string, unknown> | None
          const method = res?.extensions?.method
          const variables = log?.variables as Record<string, unknown> | None
          const context = operation.getContext()

          const logFunc = (error: boolean, type: string, msg: string, obj: Record<string, unknown>) => {
            const colors = error ? { bg: '#c0392b', fg: '#fff' } : { bg: '#2ecc71', fg: '#fff' }
            console.log(`%c → ${type.padStart(9, ' ')}`, `background: ${colors.bg}; color: ${colors.fg}; border-radius: 1px`, `  ${msg.padEnd(70, ' ')}`, obj)
          }

          const type = log?.operationType ? `${log.operationType} ` : ''

          logFunc(!!res?.errors || log?.isError === true, type, `${log?.query || log?.mutation || operation.operationName} `, {
            duration: new Date().getTime() - time,
            method,
            'cache-control': context.response?.headers?.get?.('cache-control'),
            ...(res?.errors && { errors: res?.errors }),
            ...(log && { log, db: log?.mongoOperations }),
            ...(res?.data && {
              size: helpers.tryCatch(() => JSON.stringify(res?.data).length),
              data: res?.data,
            }),
            ...(variables && { variables }),
          })
        }

        return res
      })
    )
  })
}

export function createMyApolloLink(inp: { lang: () => string; csrfCookie: () => string | undefined }): ApolloLink {
  return new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      let sub: ReturnType<ReturnType<typeof forward>['subscribe']>
      let retriedSub: ReturnType<ReturnType<typeof forward>['subscribe']>
      let closed = false
      const csrftoken1 = inp.csrfCookie()

      function errorHandler(input: { graphQLErrors: None | readonly GraphQLFormattedError[]; networkError: unknown }, log: string, fallback: () => void) {
        if (closed) return
        const csrftoken3 = inp.csrfCookie()

        if (csrftoken3 !== csrftoken1 || input.graphQLErrors?.some((i) => i.message === 'BAD_CSRF')) {
          retriedSub = subscribe(operation, csrftoken3)
          return
        }

        if (input.networkError) {
          observer.error(input.networkError)
          return
        }
        if (input.graphQLErrors?.length) {
          console.error('[apollo:input.graphQLErrors]', log, { graphQLErrors: input.graphQLErrors, csrftoken3, csrftoken1 })
        }
        fallback?.()
      }

      const counter: Record<string, number> = {}
      const inc = (name: string) => {
        counter[name] ||= 0
        counter[name] += 1
        return counter[name] > 5
      }

      function subscribe(operation: ApolloLink.Operation, csrftoken: string | undefined) {
        const cookie = import.meta.server ? useRequestHeaders(['cookie']).cookie : null
        const opContext = operation.getContext()
        operation.setContext({
          ...opContext,
          headers: {
            ...(cookie && { cookie }),
            ...opContext.headers,
            ...(csrftoken && { 'x-csrftoken': csrftoken }),
            'x-browser': import.meta.client === true ? 'true' : 'false',
            'x-lang': inp.lang(),
          },
        })

        return forward(operation).subscribe({
          next(result) {
            if (inc('next')) observer.next.bind(observer)(result)
            else if (result.errors?.length) errorHandler({ graphQLErrors: result.errors, networkError: undefined }, 'next(result)', () => observer.next(result))
            else observer.next(result)
          },
          error(networkError) {
            if (inc('error')) return observer.error.bind(observer)(networkError)
            errorHandler({ networkError, graphQLErrors: networkError?.result?.errors }, 'error(networkError)', () => observer.error.bind(observer)(networkError))
          },
          complete: () => {
            // disable the previous sub from calling complete on observable if retry is in flight.
            if (inc('complete') || !retriedSub) observer.complete.bind(observer)()
          },
        })
      }

      try {
        sub = subscribe(operation, csrftoken1)
      } catch (err) {
        errorHandler({ networkError: err as Error, graphQLErrors: undefined }, 'catch (err)', () => observer.error(err))
      }

      return () => {
        closed = true
        sub?.unsubscribe()
        retriedSub?.unsubscribe()
      }
    })
  })
}
